import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { api, ApiError, type NewView, type SavedView } from '@/api/client';
import { envStyle } from '@/lib/format';
import { QueryHighlight } from '@/lib/text';
import { presetOf } from '@/lib/time';
import { Icon } from './Icon';

export interface ViewsMenuProps {
    env: string;
    // What saving stores: the query, the range as a preset, the columns.
    current: Omit<NewView, 'name' | 'env' | 'pinned'>;
    onApply: (view: SavedView) => void;
    say: (message: string) => void;
}

// The team's saved views: a query, a time range and columns under a name. Pinned ones first; a click applies one.
export function ViewsMenu(props: ViewsMenuProps): ReactNode {
    const queryClient = useQueryClient();
    const views = useQuery({ queryKey: ['views'], queryFn: api.views, staleTime: 30_000 });
    const [open, setOpen] = useState(false);
    const [naming, setNaming] = useState(false);
    const [name, setName] = useState('');
    const [onlyHere, setOnlyHere] = useState(false);
    const [confirming, setConfirming] = useState<string | null>(null);
    const root = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!open) {
            return;
        }
        const close = (event: MouseEvent): void => {
            if (!root.current?.contains(event.target as Node)) {
                setOpen(false);
                setNaming(false);
                setConfirming(null);
            }
        };
        window.addEventListener('mousedown', close);

        return () => window.removeEventListener('mousedown', close);
    }, [open]);
    const refresh = (): Promise<void> => queryClient.invalidateQueries({ queryKey: ['views'] });
    const save = useMutation({
        mutationFn: () => api.saveView({ ...props.current, name: name.trim(), env: onlyHere ? props.env : null, pinned: false }),
        onSuccess: async () => {
            await refresh();
            props.say(`View “${name.trim()}” saved for the team`);
            setNaming(false);
            setName('');
        },
        onError: (error) => props.say(error instanceof ApiError ? error.message : 'Not saved'),
    });
    const pin = useMutation({ mutationFn: (view: SavedView) => api.updateView(view.id, { pinned: !view.pinned }), onSuccess: refresh });
    const remove = useMutation({
        mutationFn: (view: SavedView) => api.deleteView(view.id),
        onSuccess: async (_, view) => {
            await refresh();
            props.say(`View “${view.name}” deleted`);
        },
    });
    const list = (views.data?.views ?? []).filter((view) => view.env === null || view.env === props.env);

    return (
        <div className="views" ref={root}>
            <button className={`ghost tool ${open ? 'on' : ''}`} onClick={() => setOpen(!open)} title="Saved views, shared with the team">
                <Icon name="star" size={18} />
                {list.length > 0 && <span className="count">{list.length}</span>}
            </button>
            {open && (
                <div className="views-pop float">
                    <div className="rp-title">
                        Saved views <b>· shared with the team</b>
                    </div>
                    {list.length === 0 && <div className="views-empty">No view yet. Save this one: the query, the range and the columns.</div>}
                    <div className="views-list">
                        {list.map((view) => (
                            <div key={view.id} className="vrow">
                                <button
                                    className="vmain"
                                    onClick={() => {
                                        props.onApply(view);
                                        setOpen(false);
                                    }}
                                >
                                    <span className="vname">
                                        {view.pinned && <span className="vpin">★</span>}
                                        {view.name}
                                        {view.env && (
                                            <span className="venv" style={{ ['--c' as string]: envStyle(view.env).color }}>
                                                {envStyle(view.env).label}
                                            </span>
                                        )}
                                    </span>
                                    <span className="vq">{view.q ? <QueryHighlight q={view.q} /> : <i>every line</i>}</span>
                                    <span className="vmeta">
                                        {presetOf(view.range).label}
                                        {view.columns.length > 0 ? ` · ${view.columns.length} columns` : ''}
                                        {view.author ? ` · ${view.author}` : ''}
                                    </span>
                                </button>
                                <span className="vact">
                                    <button title={view.pinned ? 'Unpin' : 'Pin to the top'} onClick={() => pin.mutate(view)}>
                                        {view.pinned ? '★' : '☆'}
                                    </button>
                                    <button
                                        className={confirming === view.id ? 'danger' : ''}
                                        title={confirming === view.id ? 'Click again to delete it for everyone' : 'Delete'}
                                        onClick={() => {
                                            if (confirming === view.id) {
                                                remove.mutate(view);
                                                setConfirming(null);
                                            } else {
                                                setConfirming(view.id);
                                            }
                                        }}
                                    >
                                        {confirming === view.id ? 'Delete?' : <Icon name="x" size={13} />}
                                    </button>
                                </span>
                            </div>
                        ))}
                    </div>
                    {naming ? (
                        <form
                            className="vsave"
                            onSubmit={(event) => {
                                event.preventDefault();
                                if (name.trim()) {
                                    save.mutate();
                                }
                            }}
                        >
                            <input
                                autoFocus
                                placeholder="A name the team will recognise"
                                value={name}
                                onChange={(event) => setName(event.target.value)}
                                maxLength={80}
                            />
                            <label className="vonly">
                                <input type="checkbox" checked={onlyHere} onChange={(event) => setOnlyHere(event.target.checked)} />
                                only in {props.env}
                            </label>
                            <button className="act primary" disabled={!name.trim() || save.isPending}>
                                Save
                            </button>
                        </form>
                    ) : (
                        <button className="act vnew" onClick={() => setNaming(true)}>
                            <Icon name="plus" size={15} /> Save this view
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}
